import { Button, UploadFilledIcon } from '@calizahq/react-components-v2'
import { CommentMutations } from '@sales-app/graphql/mutations/comment.mutations'
import { Editor } from '@shared/components/Editor/Editor'
import { getGroupMentionsValues, getMentionsValues } from '@shared/components/Editor/util/Mentions.util'
import { Form, Formik, useField } from 'formik'
import { $getRoot, EditorState } from 'lexical'
import React, { FC, useState } from 'react'
import * as Yup from 'yup'

import { useAlertContext, useUserContext } from '@/context'
import { useGraphqlMutation } from '@/hooks/useGraphql'

type FormSchema = { attachments?: File[]; internalOnly: boolean }

type Props = {
  commentableId: number
  commentableType: string
  refetchOnSuccess?: () => void
}

const intialValues: FormSchema = { internalOnly: true, attachments: undefined }

export const CreateComment: FC<Props> = ({ commentableId, commentableType, refetchOnSuccess }) => {
  const [comment, setComment] = useState<EditorState>()
  const [successCount, setSuccessCount] = useState<number>(0)
  const { setAlert } = useAlertContext()

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(CommentMutations.createComment(), {
    onSuccess: () => {
      setAlert('Created new Comment!', 'success')
      refetchOnSuccess && refetchOnSuccess()
      setSuccessCount(successCount + 1)
    },
  })
  const { user } = useUserContext()
  const handleSubmit = ({ attachments, ...commentValues }: FormSchema) => {
    const textNode = comment?.read(() => {
      return $getRoot()
    })

    const text = textNode?.__cachedText || ''
    const mentionUserIds = getMentionsValues(comment?._nodeMap)
    const mentionGroupIds = getGroupMentionsValues(comment?._nodeMap)
    const richText = JSON.stringify(comment?.toJSON())
    const isEmpty = !text.trim().length

    if (!isEmpty) {
      mutate({
        comment: {
          createdBy: user?.id,
          commentableId,
          commentableType,
          mentionUserIds: mentionUserIds.length ? mentionUserIds : null,
          mentionGroupIds: mentionGroupIds.length ? mentionGroupIds : null,
          richText,
          text,
          ...commentValues,
        },
        attachments,
      })
    }
  }

  const commentStyle = 'flex w-full flex-col flex-wrap rounded-lg px-4 mt-4'
  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        ...intialValues,
      }}
      onSubmit={(values, { resetForm }) => {
        handleSubmit(values)
        resetForm({ values: { ...intialValues } })
      }}
      validateOnBlur={false}
      validationSchema={formSchema}
    >
      {({ values, errors }) => {
        const { attachments } = values
        return (
          <>
            <Editor activeCommentToReply={undefined} setComment={setComment} successCount={successCount} />
            <Form className={`${commentStyle} border-secondary`}>
              <div className="flex flex-col items-center justify-between md:flex-row ">
                <div className="mb-2 flex w-full items-center justify-between md:mb-0 md:w-auto md:justify-start">
                  <FileUploadComponent name="attachments" />
                </div>
                {errors['text'] && (
                  <p className="mt-2 font-medium text-label-sm text-semantic-error">{errors['text']}</p>
                )}
                <Button
                  className="w-full bg-text-semantic-success md:w-auto"
                  disabled={isMutationLoading}
                  size={'small'}
                  type="submit"
                >
                  Post
                </Button>
              </div>
              {attachments && attachments?.length > 0 && (
                <p className="text-p-sm">{attachments.length} files attached</p>
              )}
            </Form>
          </>
        )
      }}
    </Formik>
  )
}

function FileUploadComponent({ name }: { name: string }) {
  const [, , { setValue }] = useField<(File | { id: number; src: string })[]>({
    type: 'file',
    name,
  })

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) {
      return
    }
    setValue([...e.target.files])
  }

  return (
    <div className="flex">
      <label className=" mr-4 text-label-sm leading-120" htmlFor={name}>
        <UploadFilledIcon className="text-grey-800" width={24} />
      </label>
      <input
        accept="image/*,application/pdf"
        className="hidden"
        id={name}
        multiple={true}
        onChange={handleFileChange}
        type="file"
      ></input>
    </div>
  )
}

const formSchema = Yup.object({})
